import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/components/SurveyPage.css";
import omnicareLogo from "../assets/icons/logo.svg";
import doctorImage from "../assets/images/doctor.png";
import backIcon from "../assets/icons/back.svg";
import coffeeIcon from "../assets/icons/coffee.svg";
import starsIcon from "../assets/icons/stars.svg";
import LanguageSwitcher from "./LanguageSwitcher";

const INFO_PANEL_MAX_WIDTH = 780; // hide "Why this matters" below this (px)

const SurveyTemplate = ({
  introText,
  questionText,
  options,
  infoSections,
  onOptionSelect,
  selectedOption,
  onBack,
  customInput,
  helperText,
  progressContainer,
  navigationButtons,
  consentText,
  infoPanelLayout = "sidepanel", // deprecated - kept for backward compatibility
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isInfoExpanded, setIsInfoExpanded] = useState(false); // Default collapsed, tap ℹ️ to expand
  const [showInfoPanel, setShowInfoPanel] = useState(() => {
    if (typeof window === "undefined") return false;
    const w = window.innerWidth;
    const show = w > INFO_PANEL_MAX_WIDTH;
    console.log("[WhyThisMatters] SurveyTemplate initial:", { width: w, threshold: INFO_PANEL_MAX_WIDTH, show });
    return show;
  });

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const show = w > INFO_PANEL_MAX_WIDTH;
      setShowInfoPanel(show);
      console.log("[WhyThisMatters] SurveyTemplate resize:", { width: w, threshold: INFO_PANEL_MAX_WIDTH, show });
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  
  const handleDropdownChange = (e) => {
    onOptionSelect(e.target.value);
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  const toggleInfo = () => {
    setIsInfoExpanded(!isInfoExpanded);
  };

  return (
    <div className="survey-container">
      {/* Header */}
      <header className="survey-header">
        <div className="logo-section">
          <div
            className="logo"
            onClick={() => navigate("/landing")}
            style={{ cursor: "pointer" }}
          >
            <img src={omnicareLogo} alt="Pebble Health Logo" className="logo-icon" />
            <span className="logo-text">Pebble Health</span>
          </div>
        </div>
        <div className="header-actions">
          <LanguageSwitcher />
          <div className="back-button" onClick={handleBackToHome}>
            <img src={backIcon} alt="Back" className="back-icon" />
          </div>
        </div>
      </header>

      {/* Doctor illustration positioned on the divider line */}
      <div className="doctor-divider">
        <img src={doctorImage} alt="Dr. Sam" className="doctor-avatar" />
      </div>

      <div className="survey-content">
        {/* Doctor Introduction - on its own line */}
        <div className="doctor-intro">
          <div className="intro-header">
            <div className="intro-text-container">
              <img src={coffeeIcon} alt="Coffee" className="coffee-icon" />
              <p className="intro-text">{introText}</p>
            </div>
          </div>
        </div>

        {/* Survey Question Container */}
        <div className="survey-question-container collapsible-sidepanel-layout">
          <div className="survey-question">
            <div className="question-header">
              <h2 className="question-text">
                <span className="question-text-content">{questionText}</span>
                {infoSections && infoSections.length > 0 && (
                  <button
                    className={`info-icon-button ${isInfoExpanded ? "info-expanded" : ""}`}
                    onClick={toggleInfo}
                    aria-label="Toggle information"
                    aria-expanded={isInfoExpanded}
                  >
                    {isInfoExpanded ? (
                      <svg className="info-icon-svg info-icon-sparkles" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
                        <path d="M20 3v4"/>
                        <path d="M22 5h-4"/>
                      </svg>
                    ) : (
                      <svg className="info-icon-svg info-icon-lightbulb" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                        <path d="M9 18h6"/>
                        <path d="M10 22h4"/>
                      </svg>
                    )}
                  </button>
                )}
              </h2>
            </div>

            {helperText && (
              <p className="question-helper-text">{helperText}</p>
            )}

            <div className="options-container">
              {options.length > 0 ? (
                <select
                  className="option-dropdown"
                  value={selectedOption || ""}
                  onChange={handleDropdownChange}
                >
                  <option value="" disabled>
                    {t("survey.page1.selectPlaceholder")}
                  </option>
                  {options.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {customInput && customInput}
            </div>
          </div>

          {/* Collapsible Info Panel - inline on all screen sizes */}
          {isInfoExpanded && infoSections && infoSections.length > 0 && (
            <div className="info-panel info-panel-mobile">
              {infoSections.map((section, index) => (
                <div key={index} className="info-section">
                  <div className="info-header">
                    <svg className="star-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
                    </svg>
                    <h3>{section.title}</h3>
                  </div>
                  <p className="info-text">{section.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Progress Container Slot */}
        {progressContainer && (
          <div className="progress-container-wrapper">{progressContainer}</div>
        )}

        {/* Consent Notice — shown right above navigation buttons */}
        {consentText && (
          <div className="consent-notice">
            <span className="consent-icon">🔒</span>
            <p className="consent-text">{consentText}</p>
          </div>
        )}

        {/* Navigation Buttons Slot */}
        {navigationButtons && (
          <div className="navigation-buttons-wrapper">{navigationButtons}</div>
        )}
      </div>
    </div>
  );
};

export default SurveyTemplate;
